<template>
  <div class="card">
    <div class="card-body">

      <div class="text-center mb-4" v-if="!token || !viewMode">
        <button class="btn btn-warning" @click="autoPopulate">
          <i class="bi bi-lightning-fill me-2"></i>Auto-Populate for Demo
        </button>
      </div>

      <div class="d-flex justify-content-end mb-3" v-if="token">
        <button class="btn btn-success me-2" @click="takeAction('APPROVED')" :disabled="isTakingAction">
          <span v-if="isTakingAction">
            <span class="spinner-border spinner-border-sm me-2"></span>
            Processing...
          </span>
          <span v-else>
            <i class="bi bi-check-circle me-1"></i> Approve / Disburse
          </span>
        </button>

        <button class="btn btn-danger" @click="takeAction('REJECTED')" :disabled="isTakingAction">
          <span v-if="isTakingAction">
            <span class="spinner-border spinner-border-sm me-2"></span>
            Processing...
          </span>
          <span v-else>
            <i class="bi bi-x-circle me-1"></i> Reject
          </span>
        </button>

      </div>

      <form @submit.prevent="submitForm" novalidate>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">
              Client Name or ID <span class="text-danger">*</span>
            </label>

            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-person"></i>
              </span>

              <input type="text" list="client-suggestions" class="form-control" v-model="clientSearchTerm"
                @input="filterClients" @change="selectClientFromSearch" :class="{
                  'is-invalid': formTouched && errors.idNumber,
                  'bg-light': isClientPrefilled
                }" placeholder="Search by name or ID" autocomplete="off" :disabled="viewMode || isClientPrefilled" />

              <datalist id="client-suggestions">
                <option v-for="client in filteredClients" :key="client.id"
                  :value="`${client.fullName} (${client.idNumber})`">
                </option>
              </datalist>
            </div>

            <!-- ✅ CLIENT FINANCIAL SUMMARY (same style as guarantor) -->
            <div v-if="clientFinancialSummary && !viewMode" class="small mt-2 text-muted border rounded p-2 bg-light">

              <div>
                <strong>Savings:</strong>
                <span class="text-success ms-2">
                  {{ formatKES(clientFinancialSummary.totalSavings) }}
                </span>
              </div>

              <div>
                <strong>Loan Liability:</strong>
                <span class="text-danger ms-2">
                  {{ formatKES(clientFinancialSummary.totalLoanLiability) }}
                </span>
              </div>
              <div>
                <strong>Loans Guaranteed:</strong>
                <span class="text-primary ms-2">
                  {{ clientFinancialSummary.totalLoansGuaranteed }} loan<span
                    v-if="clientFinancialSummary.totalLoansGuaranteed !== 1">s</span>
                </span>
                <span class="text-secondary ms-3">
                  (Total: {{ formatKES(clientFinancialSummary.totalGuaranteedAmount) }})
                </span>
              </div>


            </div>
            <div v-if="loan.clientFinancialSummary && viewMode"
              class="small mt-2 text-muted border rounded p-2 bg-light">
              <div>
                <strong>Savings:</strong>
                <span class="text-success ms-2">
                  {{ formatKES(loan.clientFinancialSummary.totalSavings) }}
                </span>
              </div>
              <div>
                <strong>Loan Liability:</strong>
                <span class="text-danger ms-2">
                  {{ formatKES(loan.clientFinancialSummary.totalLoanLiability) }}
                </span>
              </div>

              <div>
                <strong>Loans Guaranteed:</strong>
                <span class="text-primary ms-2">
                  {{ loan.clientFinancialSummary.totalLoansGuaranteed }}
                </span>
              </div>
            </div>
            <div class="invalid-feedback d-block" v-if="formTouched && errors.idNumber">
              {{ errors.idNumber }}
            </div>
          </div>

          <div class="col-md-6">
            <label for="contractDate" class="form-label">Contract Status</label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-calendar"></i></span>

              <!-- Contract Date Display -->
              <input v-if="(selectedClient && selectedClient.contractDate) || (loan.contractDate)" type="text"
                class="form-control bg-light"
                :value="(selectedClient && selectedClient.contractDate) ? selectedClient.contractDate : loan.contractDate"
                readonly />

              <!-- Show pending months if any -->
              <span v-if="loan.pendingContractMonths > 0" class="input-group-text text-warning">
                {{ loan.pendingContractMonths }} Month<span v-if="loan.pendingContractMonths > 1">s </span> to End of
                Contract
              </span>

              <!-- Only show input if no contractDate and NOT in view mode -->
              <input v-else-if="!viewMode" type="month" class="form-control" v-model="loan.contractMonthYear"
                placeholder="Enter contract month/year" />
            </div>
          </div>

          <div class="col-md-6" v-if="!viewMode">
            <label for="loanClassification" class="form-label">Loan Classification <span
                class="text-danger">*</span></label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-tag"></i></span>
              <select id="loanClassification" v-model="selectedLoanClassification" class="form-control"
                :class="{ 'is-invalid': formTouched && errors.loanClassification }" :disabled="viewMode">
                <option disabled value="">Select Classification</option>
                <option value="Cash">Cash</option>
                <option value="Product">Product</option>
              </select>
            </div>
            <div class="invalid-feedback d-block" v-if="formTouched && errors.loanClassification">{{
              errors.loanClassification }}</div>
          </div>

          <div class="col-md-6">
            <label for="loanType" class="form-label">Loan Type <span class="text-danger">*</span></label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-bank"></i></span>
              <input v-if="token || viewMode" type="text" class="form-control" :value="loan.loanTypeName" disabled />
              <select v-else id="loanType" v-model.number="loan.loanTypeId" class="form-control"
                :class="{ 'is-invalid': formTouched && errors.loanTypeId }"
                :disabled="!selectedLoanClassification || (viewMode && !approvalEditMode)">
                <option disabled value="">Select Loan Type</option>
                <option v-for="type in filteredLoanTypes" :key="type.id" :value="type.id">
                  {{ type.name }}
                </option>
              </select>
            </div>
            <div class="invalid-feedback d-block" v-if="formTouched && errors.loanTypeId">{{ errors.loanTypeId }}
            </div>
          </div>

          <div
            v-if="loan.loanTypeId && (loan.loanClassification || selectedLoanType?.classification)?.toLowerCase() === 'cash'"
            class="col-md-6">
            <label for="principalAmount" class="form-label">Principal Amount <span class="text-danger">*</span></label>
            <div class="principal-amount-control">
              <div class="input-group">
                <span class="input-group-text"><i class="bi bi-cash-coin"></i></span>
                <input type="number" id="principalAmount" class="form-control" v-model.number="loan.principalAmount"
                  :min="currentMinLoanAmount" :max="currentMaxLoanAmount" :step="principalStep"
                  :class="{ 'is-invalid': formTouched && errors.principalAmount }" @input="handlePrincipalAmountInput"
                  @blur="handlePrincipalAmountBlur" :disabled="viewMode && !approvalEditMode" />
              </div>
              <input v-if="!viewMode" type="range" class="form-range principal-range-slider"
                v-model.number="loan.principalAmount" :min="currentMinLoanAmount" :max="currentMaxLoanAmount"
                :step="principalStep" :class="{ 'is-invalid': formTouched && errors.principalAmount }"
                :disabled="viewMode && !approvalEditMode" />
              <div v-if="!viewMode" class="d-flex justify-content-between range-labels">
                <span>{{ formatKES(currentMinLoanAmount) }}</span>
                <span>{{ formatKES(currentMaxLoanAmount) }}</span>
              </div>
            </div>
            <div class="invalid-feedback d-block" v-if="formTouched && errors.principalAmount">{{
              errors.principalAmount }}
            </div>
          </div>

          <div v-else-if="loan.loanTypeId && selectedLoanType?.classification === 'Product'" class="col-md-6">
            <label for="productSearch" class="form-label">Select Product <span class="text-danger">*</span></label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-box"></i></span>
              <input type="text" id="productSearch" list="product-suggestions" class="form-control"
                v-model="productSearchTerm" @input="filterProducts" @change="selectProductFromSearch"
                :class="{ 'is-invalid': formTouched && errors.selectedProductId }" placeholder="Search product"
                autocomplete="off" :disabled="viewMode" />
              <datalist id="product-suggestions">
                <option v-for="product in filteredProducts" :key="product.id"
                  :value="`${product.name} (KES ${product.sellingPrice})`"></option>
              </datalist>

            </div>
            <div class="invalid-feedback d-block" v-if="formTouched && errors.selectedProductId">{{
              errors.selectedProductId
            }}</div>
            <div class="form-text">Price will be auto-filled as principal amount.</div>
          </div>

          <div class="col-md-6" v-if="selectedLoanType?.classification === 'Product'">
            <label class="form-label">Principal (Auto-set from Product)</label>
            <div class="input-group">
              <span class="input-group-text">KES</span>
              <input type="text" class="form-control bg-light" :value="formatKES(loan.principalAmount)" readonly
                :disabled="viewMode" />
            </div>
          </div>

          <div class="col-md-12 mt-3">
            <label class="form-label">Loan Attributes/Fees</label>
            <div v-if="loanAttributes.length > 0" class="border p-3 rounded">
              <div v-for="attr in loanAttributes" :key="attr.name" class="d-flex justify-content-between mb-2">
                <span v-if="!viewMode">{{ attr.name }} ({{ attr.isPercentage ? attr.value + '%' : formatKES(attr.value)
                }}):</span>
                <span v-if="viewMode">{{ attr.name }} ({{ attr.isPercentage ? attr.originalValue + '%' :
                  formatKES(attr.value) }}):</span>
                <strong v-if="!viewMode">{{ formatKES(attr.calculated) }}</strong>
                <strong v-if="viewMode">{{ formatKES(attr.amount) }}</strong>
              </div>
            </div>
            <div v-else class="alert alert-info">No specific fees/attributes configured for this loan type.</div>
          </div>

          <!-- Repayment Term -->
          <div class="col-md-6" v-if="loan.loanTypeId">
            <label for="loanTerm" class="form-label">
              Loan Term <span class="text-danger">*</span>
            </label>

            <div class="repayment-frequency-control">
              <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-calendar-range"></i>
                </span>

                <!-- Change here -->
                <input type="number" id="loanTerm" class="form-control" v-model.number="loan.loanTerm" :min="1"
                  :max="selectedLoanType?.maxTerm" :class="{ 'is-invalid': formTouched && errors.loanTerm }"
                  :disabled="viewMode && !approvalEditMode" />

                <span class="input-group-text">
                  {{ loan.termUnit }}
                </span>
              </div>

              <!-- Slider -->
              <input v-if="!viewMode" type="range" class="form-range repayment-range-slider"
                v-model.number="loan.loanTerm" :min="1" :max="selectedLoanType?.maxTerm" step="1"
                :disabled="viewMode" />

              <div class="d-flex justify-content-between range-labels">
                <span>1 {{ loan.termUnit }}</span>
                <span>{{ selectedLoanType?.maxTerm }} {{ loan.termUnit }}</span>
              </div>
            </div>
          </div>


          <div v-if="!viewMode" class="col-md-12">
            <label for="loanPurpose" class="form-label">Loan Purpose</label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-pencil-square"></i></span>
              <textarea id="loanPurpose" v-model="loan.purpose" class="form-control" :disabled="viewMode"></textarea>
            </div>
          </div>

          <!-- ============================= -->
          <!-- GUARANTORS SECTION (EDIT MODE) -->
          <!-- ============================= -->
          <div v-if="!viewMode" class="col-12 mt-4">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0 fw-semibold">Guarantors</h6>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="addGuarantor">
                + Add
              </button>
            </div>

            <div v-if="guarantors.length === 0" class="text-muted small border rounded p-3 bg-light">
              No guarantors added.
            </div>

            <div v-else class="table-responsive border rounded">
              <table class="table table-sm align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th style="width: 50%">Guarantor</th>
                    <th style="width: 30%">Guaranteed Amount</th>
                    <th style="width: 20%" class="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(g, index) in guarantors" :key="index">
                    <td>
                      <input type="text" class="form-control form-control-sm"
                        :class="{ 'is-invalid': errors[`guarantor_${index}`] }" :list="'guarantor-list-' + index"
                        v-model="g.selection" @input="filterGuarantor(index)" @change="selectGuarantor(index)"
                        placeholder="Search name or ID" />

                      <datalist :id="'guarantor-list-' + index">
                        <option v-for="client in g.filteredOptions" :key="client.id"
                          :value="`${client.fullName} (${client.idNumber})`">
                        </option>
                      </datalist>

                      <!-- ✅ GUARANTOR FINANCIAL SUMMARY -->
                      <div v-if="g.clientId && guarantorFinancialSummaries[g.clientId]"
                        class="small mt-2 text-muted border rounded p-2 bg-light">

                        <div><strong>Savings:</strong>
                          <span class="text-success ms-2">
                            {{
                              formatKES(guarantorFinancialSummaries[g.clientId].totalSavings) }}</span>
                        </div>
                        <div><strong>Loan Liability:</strong>
                          <span class="text-danger ms-2">{{
                            formatKES(guarantorFinancialSummaries[g.clientId].totalLoanLiability) }}
                          </span>
                        </div>
                        <div><strong>Loans Guaranteed:</strong> <span class="text-primary ms-2">{{
                          guarantorFinancialSummaries[g.clientId].totalLoansGuaranteed }}</span></div>

                      </div>

                      <div v-if="errors[`guarantor_${index}`]" class="invalid-feedback d-block small">
                        {{ errors[`guarantor_${index}`] }}
                      </div>
                    </td>

                    <td>
                      <input type="number" class="form-control form-control-sm"
                        :class="{ 'is-invalid': errors[`guarantor_amount_${index}`] }"
                        v-model.number="g.guaranteedAmount" min="0" />
                      <div v-if="errors[`guarantor_amount_${index}`]" class="invalid-feedback d-block small">
                        {{ errors[`guarantor_amount_${index}`] }}
                      </div>
                    </td>

                    <td class="text-center">
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="removeGuarantor(index)">
                        Remove
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Totals -->
            <div v-if="guarantors.length > 0" class="mt-2 text-end small">
              <div class="fw-bold">Total: {{ formatKES(totalGuaranteed) }}</div>
              <div :class="remainingToGuarantee > 0 ? 'text-danger' : 'text-success'">
                Remaining: {{ formatKES(remainingToGuarantee) }}
              </div>
            </div>
          </div>

          <div v-if="viewMode" class="col-md-12 mt-4">
            <div class="section-title">Guarantor Status</div>

            <div v-if="loan.guarantors && loan.guarantors.length">
              <ul class="list-group">
                <li v-for="g in loan.guarantors" :key="g.idNumber" class="list-group-item">
                  <div><strong>{{ g.fullName }}</strong> <small class="text-muted">({{ g.idNumber }})</small></div>

                  <div v-if="g.financialSummary" class="small mt-2 text-muted border rounded p-2 bg-light">
                    <div>
                      <strong>Savings:</strong>
                      <span class="text-success ms-2">{{ formatKES(g.financialSummary.totalSavings) }}</span>
                    </div>
                    <div>
                      <strong>Loan Liability:</strong>
                      <span class="text-danger ms-2">{{ formatKES(g.financialSummary.totalLoanLiability) }}</span>
                    </div>
                    <div>
                      <strong>Loans Guaranteed:</strong>
                      <span class="text-primary ms-2">
                        {{ g.financialSummary.totalLoansGuaranteed }} loan<span
                          v-if="g.financialSummary.totalLoansGuaranteed !== 1">s</span>
                      </span>
                      <span class="text-secondary ms-3">
                        (Total: {{ formatKES(g.financialSummary.totalGuaranteedAmount) }})
                      </span>
                    </div>
                  </div>

                  <div class="mt-2">
                    <strong>Guaranteed Amount:</strong>
                    <span v-if="g.guaranteedAmount > 0">{{ formatKES(g.guaranteedAmount) }}</span>
                    <span v-else class="text-muted small">not declared</span>
                  </div>

                  <div class="mt-1">
                    <span v-if="g.status === 'CONFIRMED'" class="badge bg-success">✔ Confirmed</span>
                    <span v-else class="badge bg-warning text-dark">⏳ Pending</span>
                  </div>
                </li>
              </ul>
            </div>

            <div v-else class="text-muted">No guarantors assigned.</div>
          </div>
          <!-- VIEW MODE APPROVAL STATUS -->
          <div v-if="viewMode" class="col-md-12 mt-4">
            <div class="section-title">Approval Progress</div>

            <div v-if="loan.approvalSteps && loan.approvalSteps.length">
              <ul class="list-group">
                <li v-for="step in loan.approvalSteps" :key="step.approverName"
                  class="list-group-item d-flex justify-content-between align-items-center">

                  <span>
                    {{ step.approverName }}
                  </span>

                  <span>
                    <span v-if="step.status === 'APPROVED'" class="badge bg-success">
                      ✔ Approved
                    </span>
                    <span v-else class="badge bg-warning text-dark">
                      ⏸Locked || ⏳Pending
                    </span>
                  </span>

                </li>
              </ul>
            </div>

            <div v-else class="text-muted">
              No approval steps yet.
            </div>
          </div>

          <div class="col-md-6 mt-3">
            <label class="form-label">Total Payable Amount</label>
            <div class="input-group">
              <span class="input-group-text">KES</span>
              <input type="text" class="form-control bg-light" :value="formatKES(totalPayable)" readonly
                :disabled="viewMode" />
            </div>
          </div>

          <div class="col-md-12 mt-4" v-if="repaymentSchedule.length">
            <label class="form-label">Repayment Schedule</label>
            <div class="table-responsive">
              <table class="table table-bordered table-striped">
                <thead class="table-light">
                  <tr>
                    <th>#</th>
                    <th>Due Date</th>
                    <th>Opening Balance</th>
                    <th>Principal</th>
                    <th>Interest</th>
                    <th>Insurance</th>
                    <th>P + I</th>
                    <th>Installment Total</th>
                    <th>Standard Amount Due</th>
                    <th>Closing Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(repayment, index) in repaymentSchedule" :key="index">
                    <td>{{ repayment.installmentNo }}</td>
                    <td>{{ formatDate(repayment.dueDate) }}</td>
                    <td>{{ formatKES(repayment.openingBalance) }}</td>
                    <td>{{ formatKES(repayment.principal) }}</td>
                    <td>{{ formatKES(repayment.interest) }}</td>
                    <td>{{ formatKES(repayment.insurance) }}</td>
                    <td>{{ formatKES(repayment.pmt) }}</td>
                    <td>{{ formatKES(repayment.totalAmount) }}</td>
                    <td>{{ formatKES(repayment.amountDue) }}</td>
                    <td>{{ formatKES(repayment.closingBalance) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>

        <div class="d-flex justify-content-end mt-4">
          <button v-if="!viewMode" type="submit" class="btn btn-success" :disabled="isSubmitting">
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bi bi-check-circle me-2"></i>
            Submit Loan
          </button>

          <button v-else-if="!token" class="btn btn-secondary" @click="$router.back()">
            <i class="bi bi-arrow-left me-2"></i>
            Back to Loans
          </button>
        </div>

      </form>

      <Loans v-if="!viewMode" class="col-12 mt-5" />
    </div>
  </div>
</template>

<script>
import loanApi from "@/api/loanApi";
import loanTypeApi from "@/api/loanTypeApi";
import approvalApi from "@/api/approvalApi";
import clientApi from "@/api/clientApi";
import userApi from "@/api/userApi";
import swal from '@/utils/swal';
import Loans from "../../components/tables/allloans.vue";
import { productApi } from "@/api/productApi";

export default {
  components: { Loans },
  data() {
    return {
      guarantorFinancialSummaries: {},
      clientFinancialSummary: null,
      guarantors: [],
      isTakingAction: false,
      token: null,
      approvalEditMode: false,
      approvalType: '',
      roleLabel: '',
      viewMode: false,
      loadingLoan: false,
      repaymentSchedule: [], // new: stores amortization table returned from backend
      loan: {
        idNumber: '',
        loanTypeId: '',
        principalAmount: null,
        loanTerm: null,
        termUnit: 'Months',
        repaymentFrequency: 1,
        purpose: '',
        requestedByUserId: null,
        contractDate: null,
        contractMonthYear: null,
        pendingContractMonths: null
      },
      selectedProductId: '',
      allProducts: [],
      filteredProducts: [],
      productSearchTerm: '',
      errors: {},
      clients: [],
      allLoanTypes: [],
      filteredLoanTypes: [],
      selectedLoanClassification: '',
      loanAttributes: [],
      clientSearchTerm: '',
      filteredClients: [],
      isSubmitting: false,
      formTouched: false,
      principalStep: 1,
      // Add a new property to track if the client field is pre-filled
      isClientPrefilled: false,
      selectedClient: null,
    };
  },
  computed: {
    totalGuaranteed() {
      return this.guarantors.reduce((sum, g) => {
        return sum + (g.guaranteedAmount || 0);
      }, 0);
    },

    remainingToGuarantee() {
      return (this.loan.principalAmount || 0) - this.totalGuaranteed;
    },
    selectedLoanType() {
      return this.allLoanTypes.find(t => t.id === this.loan.loanTypeId) || null;
    },
    currentMinLoanAmount() {
      return this.selectedLoanType?.minAmount ? parseFloat(this.selectedLoanType.minAmount) : 0;
    },
    currentMaxLoanAmount() {
      return this.selectedLoanType?.maxAmount ? parseFloat(this.selectedLoanType.maxAmount) : 1000000;
    },
    totalPayable() {
      return this.repaymentSchedule.reduce(
        (sum, r) => sum + (r.totalAmount || 0),
        0
      ).toFixed(2);
    }
  },
  watch: {
    selectedClient(newClient) {
      if (newClient) {
        this.loan.contractDate = newClient.contractDate || null;
        this.loan.pendingContractMonths = newClient.pendingContractMonths || null;
      } else {
        this.loan.contractDate = null;
        this.loan.pendingContractMonths = null;
      }
    },
    '$route.query.loanId': {
      immediate: true,
      async handler(loanId) {
        // Only run when viewing a loan
        if (this.$route.query.view === 'true' && loanId) {
          this.viewMode = true;
          await this.loadLoanForView(loanId);
        }
      }
    },

    selectedLoanClassification(newVal) {
      if (this.viewMode) return;
      this.handleLoanClassificationChange(newVal);
    },

    'loan.loanTypeId'(newVal) {
      if (this.viewMode) return;
      this.handleLoanTypeChange(newVal);
    },

    // Recalculate fees when principal amount changes
    'loan.principalAmount'() {
      if (this.viewMode && !this.approvalEditMode) return;
      this.recalculateLoanAttributes();
    },

    // Recalculate fees when repayment frequency changes
    'loan.repaymentFrequency'() {
      if (this.viewMode) return;
      this.recalculateLoanAttributes();
    },

    // Deep watch for any other loan changes
    loan: {
      handler() {
        if (this.viewMode && !this.approvalEditMode) return;
        this.recalculateLoanAttributes();
      },
      deep: true
    }
  },
  // async mounted() {
  //   // Wait for the clients to be fetched before attempting to pre-populate

  //   if (this.$route.query.view === 'true' && this.$route.query.loanId) {
  //     this.viewMode = true;
  //     await this.loadLoanForView(this.$route.query.loanId);
  //   }

  //   this.token = this.$route.query.token || this.$route.params.token;

  //   if (this.token) {
  //     await this.loadApprovalByToken(this.token);
  //     return;
  //   }

  //   // Now, the `this.clients` array is guaranteed to be populated
  //   else if (this.$route.query.idNumber) {
  //     const prefillIdNumber = this.$route.query.idNumber;
  //     const clientToPrefill = this.clients.find(c => c.idNumber === prefillIdNumber);
  //     if (clientToPrefill) {
  //       this.clientSearchTerm = `${clientToPrefill.fullName} (${clientToPrefill.idNumber})`;
  //       this.loan.idNumber = clientToPrefill.idNumber;
  //       this.formTouched = true;
  //       // Lock the field once it's pre-populated
  //       this.isClientPrefilled = true;
  //     }
  //   }

  //   if (!this.viewMode) {
  //     this.getCurrentUser();
  //   }
  //   if (!this.viewMode) {
  //     await this.fetchClients();
  //   }

  //   if (!this.viewMode) {
  //     await this.fetchLoanTypes();
  //   }

  // },

  async mounted() {

    // View mode first
    if (this.$route.query.view === 'true' && this.$route.query.loanId) {
      this.viewMode = true;
      await this.loadLoanForView(this.$route.query.loanId);
    }

    this.token = this.$route.query.token || this.$route.params.token;

    if (this.token) {
      await this.loadApprovalByToken(this.token);
      return;
    }

    // 🔥 IMPORTANT: fetch base data first
    if (!this.viewMode) {
      await this.fetchClients();
      await this.fetchLoanTypes();
      await this.getCurrentUser();
    }

    // 🔥 NOW safe to prefill
    const prefillIdNumber = this.$route.query.idNumber;

    if (prefillIdNumber) {
      const clientToPrefill = this.clients.find(
        c => c.idNumber === prefillIdNumber
      );

      if (clientToPrefill) {
        this.selectedClient = clientToPrefill;
        this.loan.idNumber = clientToPrefill.idNumber;
        this.clientSearchTerm =
          `${clientToPrefill.fullName} (${clientToPrefill.idNumber})`;

        this.isClientPrefilled = true;
        this.formTouched = true;

        // 🔥 load financial summary
        await this.fetchClientFinancialSummary(clientToPrefill.id);
      }
    }
  },
  methods: {
    async fetchClientFinancialSummary(clientId, isGuarantor = false) {
      try {
        const res = await clientApi.getFinancialSummary(clientId);
        if (isGuarantor) {
          this.$set(this.guarantorFinancialSummaries, clientId, res.data);
        } else {
          this.clientFinancialSummary = res.data;
        }
      } catch (e) {
        console.error("Failed to fetch financial summary");
        this.clientFinancialSummary = null;
      }
    },

    addGuarantor() {
      this.guarantors.push({
        clientId: null,
        selection: '',
        guaranteedAmount: null,
        filteredOptions: [...this.clients]
      });
    },

    removeGuarantor(index) {
      const removed = this.guarantors[index];

      if (removed?.clientId) {
        delete this.guarantorFinancialSummaries[removed.clientId];
      }

      this.guarantors.splice(index, 1);
    },

    filterGuarantor(index) {
      const query = this.guarantors[index].selection.toLowerCase();

      this.guarantors[index].filteredOptions = this.clients.filter(c =>
        c.fullName.toLowerCase().includes(query) ||
        c.idNumber.toLowerCase().includes(query)
      );
    },

    async takeAction(actionType) {
      if (!this.token) return;

      try {
        this.isTakingAction = true;
        this.$swal.fire({
          title: 'Processing...',
          text: actionType === 'APPROVED'
            ? 'Approving loan. Please wait...'
            : 'Rejecting loan. Please wait...',
          allowOutsideClick: false,
          didOpen: () => this.$swal.showLoading()
        });

        // ✅ Prepare edited payload only if editable
        let editedPayload = null;
        if (this.approvalEditMode) {
          editedPayload = {
            principalAmount: this.loan.principalAmount,
            loanTerm: this.loan.loanTerm
          };
        }

        await approvalApi.takeAction(this.token, actionType, editedPayload);

        this.$swal.close();
        await this.$swal.fire({
          icon: 'success',
          title: 'Success!',
          text: actionType === 'APPROVED'
            ? 'Loan approved successfully.'
            : 'Loan has been rejected successfully.',
          timer: 1500,
          showConfirmButton: false
        });

        window.close();
      } catch (error) {
        this.$swal.close();
        await this.$swal.fire({
          icon: 'error',
          title: 'Action Failed',
          text: error?.response?.data?.message || 'Failed to record action.',
          timer: 2000,
          showConfirmButton: false
        });
        window.close();
      } finally {
        this.isTakingAction = false;
      }
    },
    async loadApprovalByToken(token) {
      this.viewMode = true;

      const res = await approvalApi.getByToken(token);
      const data = res.data;

      this.approvalType = data.type;
      this.roleLabel = data.roleLabel;
      this.approvalEditMode = data.editable;

      await this.loadLoanForView(data.loanId);
    },
    async loadLoanForView(loanId) {
      try {
        this.loadingLoan = true;

        const res = await loanApi.getViewById(loanId);
        const loan = res.data;

        // 1️⃣ classification first
        this.selectedLoanClassification = loan.loanClassification;

        // 2️⃣ filter loan types synchronously
        this.filteredLoanTypes = this.allLoanTypes.filter(
          t => t.classification === loan.loanClassification
        );

        // 1️⃣ Map guarantors for view mode financial summary
        this.guarantorFinancialSummaries = {};
        loan.guarantors?.forEach(g => {
          if (g.financialSummary) {
            this.$set(this.guarantorFinancialSummaries, g.id, g.financialSummary);
          }
        });

        // 3️⃣ set loan object (without loanTypeId yet)
        this.loan = {
          ...loan,
        };

        this.loan.loanTypeId = loan.loanTypeId; // 5️⃣ set loan type after classification and filtering

        // 4️⃣ force DOM update
        await this.$nextTick();

        // 7️⃣ client display
        this.clientSearchTerm = `${loan.clientName} (${loan.idNumber})`;

        // 8️⃣ guarantors
        this.guarantorSelections = loan.guarantors?.map(
          g => `${g.fullName} (${g.idNumber})`
        ) || [];

        // 9️⃣ static data
        this.loanAttributes = loan.loanFees || [];
        this.repaymentSchedule = loan.repaymentSchedule || [];

      } catch (e) {
        swal.error('Failed to load loan', e.message);
      } finally {
        this.loadingLoan = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-KE');
    },

    filterGuarantorClients(index) {
      const search = this.guarantorSelections[index].toLowerCase();
      this.filteredGuarantorOptions[index] = this.clients.filter(c =>
        c.fullName.toLowerCase().includes(search) || c.idNumber.toLowerCase().includes(search)
      );
    },
    selectGuarantor(index) {
      const selectedText = this.guarantors[index].selection;

      const match = this.clients.find(c =>
        `${c.fullName} (${c.idNumber})` === selectedText
      );

      if (!match) {
        this.guarantors[index].clientId = null;
        return;
      }

      // Prevent duplicate guarantors
      const duplicate = this.guarantors.some((g, i) =>
        i !== index && g.clientId === match.id
      );

      if (duplicate) {
        this.errors[`guarantor_${index}`] =
          "This client is already selected as a guarantor.";
        this.guarantors[index].selection = '';
        this.guarantors[index].clientId = null;
        return;
      }

      this.errors[`guarantor_${index}`] = null;
      this.guarantors[index].clientId = match.id;

      this.fetchClientFinancialSummary(match.id, true);
    },

    // validateGuarantors() {
    //   this.errors = {};
    //   let isValid = true;

    //   this.guarantors.forEach((g, index) => {

    //     if (!g.clientId) {
    //       this.errors[`guarantor_${index}`] =
    //         "Guarantor must be selected.";
    //       isValid = false;
    //     }

    //     if (!g.guaranteedAmount || g.guaranteedAmount <= 0) {
    //       this.errors[`guarantor_amount_${index}`] =
    //         "Guaranteed amount must be greater than 0.";
    //       isValid = false;
    //     }

    //   });

    //   return isValid;
    // },

    handleRepaymentFrequencyInput() {
      let value = parseInt(this.loan.repaymentFrequency);
      if (isNaN(value)) {
        this.loan.repaymentFrequency = null;
      }
      this.validateForm(); // show real-time error
    },

    handleRepaymentFrequencyBlur() {
      let value = parseInt(this.loan.repaymentFrequency);

      if (isNaN(value) || value === null) {
        this.loan.repaymentFrequency = 1; // default
      } else if (value < 1) {
        this.loan.repaymentFrequency = 1;
      } else if (value > this.loan.loanTerm) {
        this.loan.repaymentFrequency = this.loan.loanTerm;
      }

      this.validateForm(); // update error state
    },
    setSelectedProduct(product) {
      if (product) {
        this.selectedProductId = product.id;
        this.loan.principalAmount = parseFloat(product.sellingPrice);
        this.productSearchTerm = `${product.name} (KES ${product.sellingPrice})`;
        this.errors.selectedProductId = '';
        this.recalculateLoanAttributes();
      } else {
        this.selectedProductId = '';
        this.loan.principalAmount = null;
        this.errors.selectedProductId = 'Please select a valid product from the suggestions.';
      }
    },

    // New: Allows typing freely without immediate clamping
    handlePrincipalAmountInput() {
      // Ensure the v-model updates as a number, but do NOT enforce min/max here.
      // The validation message will appear if out of range, but the user can continue typing.
      let value = parseFloat(this.loan.principalAmount);
      if (isNaN(value)) {
        this.loan.principalAmount = null; // Set to null if non-numeric to avoid NaN issues
      }
      // The watcher for loan.principalAmount will handle recalculation.
      // Error messages will appear if formTouched is true and validateForm reflects the current invalid state.
      this.validateForm(); // Call validate to show real-time error message during typing
    },

    // New: Clamps the value to min/max only when the user finishes editing the field
    handlePrincipalAmountBlur() {
      let value = parseFloat(this.loan.principalAmount);

      if (isNaN(value) || value === null) {
        // If the field is left empty or with an invalid number, default to min amount
        this.loan.principalAmount = this.currentMinLoanAmount;
      } else if (value < this.currentMinLoanAmount) {
        this.loan.principalAmount = this.currentMinLoanAmount;
      } else if (value > this.currentMaxLoanAmount) {
        this.loan.principalAmount = this.currentMaxLoanAmount;
      }
      // After clamping, re-run validation to ensure error state is updated
      this.validateForm();
    },

    validateForm() {
      this.errors = {};
      let isValid = true;

      if (!this.loan.idNumber) {
        this.errors.idNumber = "Client is required.";
        isValid = false;
      }
      if (!this.selectedLoanClassification) {
        this.errors.loanClassification = "Loan Classification is required.";
        isValid = false;
      }
      if (!this.loan.loanTypeId) {
        this.errors.loanTypeId = "Loan Type is required.";
        isValid = false;
      }

      if (this.selectedLoanType?.classification === 'Cash') {
        if (this.loan.principalAmount === null || isNaN(this.loan.principalAmount) || this.loan.principalAmount <= 0) {
          this.errors.principalAmount = "Principal Amount is required and must be positive.";
          isValid = false;
        } else if (this.loan.principalAmount < this.currentMinLoanAmount || this.loan.principalAmount > this.currentMaxLoanAmount) {
          this.errors.principalAmount = `Amount must be between ${this.formatKES(this.currentMinLoanAmount)} and ${this.formatKES(this.currentMaxLoanAmount)}.`;
          isValid = false;
        }
      } else if (this.selectedLoanType?.classification === 'Product') {
        if (!this.selectedProductId) {
          this.errors.selectedProductId = "Product selection is required.";
          isValid = false;
        }
      }

      // ✅ New validation for repayment frequency
      if (this.loan.repaymentFrequency === null || this.loan.repaymentFrequency < 1 || this.loan.repaymentFrequency > this.loan.loanTerm) {
        this.errors.repaymentFrequency = `Repayment frequency must be between 1 and ${this.loan.loanTerm} ${this.loan.termUnit}.`;
        isValid = false;
      }

      return isValid;
    },


    handleLoanClassificationChange(newClassification) {
      this.loan.loanTypeId = '';
      this.loan.principalAmount = null;
      this.selectedProductId = '';
      this.productSearchTerm = '';
      this.loanAttributes = [];
      this.allProducts = [];
      this.filteredProducts = [];

      if (newClassification) {
        this.filteredLoanTypes = this.allLoanTypes.filter(type => type.classification === newClassification);
      } else {
        this.filteredLoanTypes = [];
      }
      if (this.errors.loanTypeId) {
        delete this.errors.loanTypeId;
      }
    },

    async handleLoanTypeChange(newTypeId) {
      const selected = this.allLoanTypes.find(t => t.id === newTypeId);
      if (!selected) {
        this.allProducts = [];
        this.filteredProducts = [];
        this.loanAttributes = [];
        this.loan.principalAmount = null;
        this.selectedProductId = '';
        this.productSearchTerm = '';
        this.guarantorSelections = [];
        this.filteredGuarantorOptions = [];
        return;
      }

      // reset arrays
      if (selected.requireGuarantors) {
        this.guarantorSelections = Array(selected.requiredGuarantorCount).fill('');
        this.filteredGuarantorOptions = Array(selected.requiredGuarantorCount).fill([...this.clients]);
      } else {
        this.guarantorSelections = [];
        this.filteredGuarantorOptions = [];
      }

      if (selected.classification !== this.selectedLoanClassification) {
        this.loan.loanTypeId = '';
        return;
      }

      this.selectedProductId = '';
      this.loan.principalAmount = null;
      this.allProducts = [];
      this.filteredProducts = [];
      this.productSearchTerm = '';

      this.loan.loanTerm = selected.maxTerm;
      this.loan.termUnit = selected.termUnit;

      await this.fetchLoanAttributes(newTypeId);

      if (selected.classification === 'Product') {
        await this.fetchProductsForLoanType(selected.minAmount, selected.maxAmount);
      } else {
        // Only set default principal if it's currently null or 0, to not override user's partial input
        if (this.loan.principalAmount === null || this.loan.principalAmount === 0) {
          this.loan.principalAmount = this.currentMinLoanAmount;
        }
      }
    },
    async fetchLoanAttributes(typeId) {
      try {
        const res = await loanTypeApi.getAttributesByTypeId(typeId);
        this.loanAttributes = res.data.map(attr => ({
          name: attr.name,
          value: parseFloat(attr.value),
          isPercentage: attr.isPercentage,
          chargeType: attr.chargeType,
          oneTimeTiming: attr.oneTimeTiming,
          calculated: 0
        }));
        this.recalculateLoanAttributes();
      } catch (e) {
        swal.error("Failed to load loan attributes", e.message);
        this.loanAttributes = [];
      }
    },
    async fetchProductsForLoanType(minLoanAmount, maxLoanAmount) {
      try {
        const res = await productApi.getByPriceRange(minLoanAmount, maxLoanAmount);
        this.allProducts = res.data;
        this.filteredProducts = [...res.data];
      } catch (e) {
        swal.error("Failed to load products for the given loan amount range", e.message);
        this.allProducts = [];
        this.filteredProducts = [];
      }
    },

    async recalculateLoanAttributes() {
      if (this.viewMode && !this.approvalEditMode) return;

      try {
        const loanData = {
          ...this.loan,

          // ✅ keep numeric values correct
          repaymentFrequency: parseInt(this.loan.repaymentFrequency),
          loanTerm: parseInt(this.loan.loanTerm),
          termUnit: this.loan.termUnit,

          loanFees: this.loanAttributes.map(attr => ({
            name: attr.name,
            amount: attr.calculated,
            isPercentage: attr.isPercentage,
            originalValue: attr.value
          })),

          totalPayableAmount: this.totalPayable,
          selectedProductId: this.selectedProductId,
          creationDate: new Date().toISOString().split('T')[0],
          startDate: new Date().toISOString().split('T')[0],
          endDate: '',
          paymentMethods: ['Bank Transfer'],
          repaymentAccount: 'AUTO_GENERATED',
        };

        const response = await loanApi.calculate(loanData);
        this.repaymentSchedule = response.data;

      } catch (e) {
        console.error("Error recalculating loan attributes:", e.message || e);
      }
    },

    async getCurrentUser() {
      try {
        const res = await userApi.getCurrentUser();
        if (res.data?.id) {
          this.loan.requestedByUserId = parseInt(res.data.id);
        } else {
          swal.error("Could not fetch user ID. Please log in again.");
        }
      } catch (e) {
        swal.error("Error fetching current user", e.message);
      }
    },
    formatKES(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value || 0);
    },
    async fetchClients() {
      try {
        const res = await clientApi.getAllforLoan();
        this.clients = res.data;
        this.filteredClients = res.data;
      } catch (e) {
        swal.error("Error fetching clients", e.message);
      }
    },
    async fetchLoanTypes() {
      try {
        const res = await loanTypeApi.getAll();
        this.allLoanTypes = res.data;
      } catch (e) {
        swal.error("Error fetching loan types", e.message);
      }
    },
    async submitForm() {
      this.formTouched = true;
      // Ensure blur logic is applied one last time before final validation
      if (this.selectedLoanType?.classification === 'Cash') {
        this.handlePrincipalAmountBlur();
      }

      if (!this.validateForm()) {
        swal.error("Validation Error", "Please correct the errors in the form.");
        return;
      }

      this.isSubmitting = true;
      try {

        // Validate guarantors first
        // if (!this.validateGuarantors()) {
        //   swal.error("Validation Error", "Please fix guarantor errors.");
        //   this.isSubmitting = false;
        //   return;
        // }

        // Build guarantor payload including amounts
        const guarantorsPayload = this.guarantors.map(g => ({
          clientId: g.clientId,
          guaranteedAmount: g.guaranteedAmount
        }));

        const guarantorIds = this.guarantors.map(g => g.clientId);

        const loanData = {
          ...this.loan,

          // ✅ correct mapping
          repaymentFrequency: parseInt(this.loan.repaymentFrequency),
          loanTerm: parseInt(this.loan.loanTerm),
          termUnit: this.loan.termUnit,

          loanFees: this.loanAttributes.map(attr => ({
            name: attr.name,
            amount: attr.calculated,
            isPercentage: attr.isPercentage,
            originalValue: attr.value
          })),

          totalPayableAmount: this.totalPayable,
          selectedProductId: this.selectedProductId,
          guarantorIds,
          creationDate: new Date().toISOString().split('T')[0],
          startDate: new Date().toISOString().split('T')[0],
          endDate: '',
          paymentMethods: ['Bank Transfer'],
          repaymentAccount: 'AUTO_GENERATED',
          guarantors: guarantorsPayload
        };

        await loanApi.create(loanData);
        swal.success('Loan submitted successfully!');
        this.resetForm();
      } catch (e) {
        swal.error('Submission failed', e.message || 'An unexpected error occurred.');
      } finally {
        this.isSubmitting = false;
      }
    },

    resetForm() {
      this.loan = {
        idNumber: '',
        loanTypeId: '',
        principalAmount: null,
        loanTerm: null,
        termUnit: 'Months',
        repaymentFrequency: 1,   // ✅ MUST BE NUMBER
        purpose: '',
        requestedByUserId: this.loan.requestedByUserId,
      };

      this.selectedProductId = '';
      this.allProducts = [];
      this.filteredProducts = [];
      this.productSearchTerm = '';
      this.errors = {};
      this.loanAttributes = [];
      this.clientSearchTerm = '';
      this.filteredClients = [...this.clients];
      this.selectedLoanClassification = '';
      this.filteredLoanTypes = [];
      this.formTouched = false;
      this.isClientPrefilled = false;
    },

    autoPopulate() {
      const demoClient = this.clients[0] || { idNumber: 'DEMO123', fullName: 'Demo Client' };

      const cashLoanType = this.allLoanTypes.find(t => t.classification === 'Cash' && t.minAmount && t.maxAmount);
      const productLoanType = this.allLoanTypes.find(t => t.classification === 'Product');

      let classificationToUse = '';
      let loanTypeIdToUse = '';
      let principalAmountToUse = null;
      let selectedProductIdToUse = '';
      let productSearchTermToUse = '';

      if (productLoanType && this.allProducts.length > 0) {
        classificationToUse = 'Product';
        loanTypeIdToUse = productLoanType.id;
        const suitableProduct = this.allProducts.find(p =>
          parseFloat(p.sellingPrice) >= parseFloat(productLoanType.minAmount) &&
          parseFloat(p.sellingPrice) <= parseFloat(productLoanType.maxAmount)
        );
        if (suitableProduct) {
          selectedProductIdToUse = suitableProduct.id;
          principalAmountToUse = parseFloat(suitableProduct.sellingPrice);
          productSearchTermToUse = `${suitableProduct.name} (KES ${suitableProduct.sellingPrice})`;
        } else {
          selectedProductIdToUse = this.allProducts[0].id;
          principalAmountToUse = parseFloat(this.allProducts[0].sellingPrice);
          productSearchTermToUse = `${this.allProducts[0].name} (KES ${this.allProducts[0].sellingPrice})`;
        }
      } else if (cashLoanType) {
        classificationToUse = 'Cash';
        loanTypeIdToUse = cashLoanType.id;
        principalAmountToUse = parseFloat(cashLoanType.minAmount);
      } else if (this.allLoanTypes.length > 0) {
        classificationToUse = this.allLoanTypes[0].classification;
        loanTypeIdToUse = this.allLoanTypes[0].id;
        if (classificationToUse === 'Cash') {
          principalAmountToUse = parseFloat(this.allLoanTypes[0].minAmount || 5000);
        } else if (classificationToUse === 'Product' && this.allProducts.length > 0) {
          selectedProductIdToUse = this.allProducts[0].id;
          principalAmountToUse = parseFloat(this.allProducts[0].sellingPrice);
          productSearchTermToUse = `${this.allProducts[0].name} (KES ${this.allProducts[0].sellingPrice})`;
        } else {
          principalAmountToUse = 5000;
        }
      }

      this.selectedLoanClassification = classificationToUse;

      this.$nextTick(async () => {
        this.loan = {
          ...this.loan,
          idNumber: demoClient.idNumber,
          loanTypeId: loanTypeIdToUse,
          principalAmount: principalAmountToUse,
          loanTerm: 12,
          termUnit: 'Months',
          repaymentFrequency: 'Monthly',
          purpose: 'General expenses and investment',
          requestedByUserId: this.loan.requestedByUserId,
        };

        this.selectedProductId = selectedProductIdToUse;
        this.clientSearchTerm = `${demoClient.fullName} (${demoClient.idNumber})`;
        this.productSearchTerm = productSearchTermToUse;

        if (loanTypeIdToUse) {
          await this.handleLoanTypeChange(loanTypeIdToUse);
        }

        this.formTouched = false;
        this.isClientPrefilled = true;
      });
    },
    filterClients() {
      if (this.isClientPrefilled) return; // Prevent filtering if pre-filled

      const searchTermLower = this.clientSearchTerm.toLowerCase();
      this.filteredClients = this.clients.filter(client =>
        client.fullName.toLowerCase().includes(searchTermLower) ||
        client.idNumber.toLowerCase().includes(searchTermLower)
      );
    },
    selectClientFromSearch() {
      const selectedValue = this.clientSearchTerm;
      const match = selectedValue.match(/\((\d+)\)$/);

      let selectedClient = null;
      if (match && match[1]) {
        selectedClient = this.clients.find(c => c.idNumber === match[1]);
      } else {
        selectedClient = this.clients.find(c =>
          c.fullName.toLowerCase() === selectedValue.toLowerCase() ||
          c.idNumber.toLowerCase() === selectedValue.toLowerCase()
        );
      }

      if (selectedClient) {
        this.selectedClient = selectedClient; // <-- assign here
        this.loan.idNumber = selectedClient.idNumber;
        this.clientSearchTerm = `${selectedClient.fullName} (${selectedClient.idNumber})`;
        this.errors.idNumber = '';

        // 🔥 ADD THIS LINE
        this.fetchClientFinancialSummary(selectedClient.id);
      } else {
        this.loan.idNumber = '';
        this.clientFinancialSummary = null; // clear summary if invalid
        this.errors.idNumber = 'Please select a valid client from the suggestions.';
      }
    },
    filterProducts() {
      const searchTermLower = this.productSearchTerm.toLowerCase();
      this.filteredProducts = this.allProducts.filter(product =>
        product.name.toLowerCase().includes(searchTermLower) ||
        product.sellingPrice.toString().includes(searchTermLower)
      );
    },
    selectProductFromSearch() {
      const selectedValue = this.productSearchTerm;
      let selectedProduct = null;

      selectedProduct = this.allProducts.find(p => `${p.name} (KES ${p.sellingPrice})` === selectedValue);

      if (!selectedProduct) {
        const match = selectedValue.match(/\(KES ([\d.]+)\)$/);
        if (match && match[1]) {
          const price = parseFloat(match[1]);
          selectedProduct = this.allProducts.find(p =>
            parseFloat(p.sellingPrice) === price && selectedValue.includes(p.name)
          );
        }
      }

      if (!selectedProduct) {
        selectedProduct = this.allProducts.find(p =>
          selectedValue.toLowerCase().includes(p.name.toLowerCase())
        );
      }

      this.setSelectedProduct(selectedProduct);
    }
  }
};
</script>

<style scoped>
/* Scoped styles for this component */

.principal-amount-control {
  display: flex;
  flex-direction: column;
}

.principal-range-slider {
  margin-top: 0;
}

.principal-amount-control .input-group {
  width: 100%;
}
</style>
